import type * as React from 'react';

// The text alternative is what these components decide, so nothing else may
// set or override it.
type ImageProps = Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  'alt' | 'aria-hidden' | 'aria-label' | 'aria-labelledby' | 'role'
>;

/**
 * An image that says nothing the text beside it does not: a show's cover next
 * to its name, or an illustration above a heading. `alt=""` takes it out of the
 * accessibility tree, so a screen reader skips it instead of reading out its
 * file name (WCAG 1.1.1).
 *
 * Use this or `IllustrativeImage` instead of a bare `<img>`, so that every
 * image is a choice somebody made.
 */
export const DecorativeImage = (props: ImageProps) => <img {...props} alt="" />;

/**
 * An image that tells a sighted reader something the text around it does not.
 * `altText` is read in its place, so write what the image says, not what it
 * looks like, and pass it through `intl.formatMessage`. An image with nothing
 * to say is a `DecorativeImage`, not one with empty `altText`.
 */
export const IllustrativeImage = ({
  altText,
  ...props
}: ImageProps & {altText: string}) => <img {...props} alt={altText} />;
